// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../Service/crypto';
import { BotServicePath, BotWsPath } from '../../Service/chatservice';
// import chatservice from '../../service/chatservice';

// Function to format bot response with headings and bullets
const formatResponse = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, index, arr) => {
        let formattedLine = line;
        let isHeading = false;
        let isBullet = false;

        const trimmedLine = line.trim();

        // Check for headings (starts with ** or ##)
        if (trimmedLine.startsWith('**') || trimmedLine.startsWith('##')) {
            isHeading = true;
            formattedLine = trimmedLine.replace(/^[\*\#]{2}\s*/, '').replace(/[\*\#]{2}$/, '').trim();
        }
        // Check for bullets (starts with * or #)
        else if (trimmedLine.startsWith('*') || trimmedLine.startsWith('#')) {
            isBullet = true;
            formattedLine = trimmedLine.replace(/^[\*\#]\s*/, '').trim();
        }

        // Inline bold formatting for **text**
        const renderInlineFormatting = (str) => {
            const parts = str.split(/(\*\*.*?\*\*)/g);
            return parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={i}>{part.slice(2, -2)}</strong>;
                }
                return part;
            });
        };

        if (isHeading) {
            return (
                <div key={index} style={{ fontWeight: 'bold', fontSize: '1.1em', marginTop: '10px', marginBottom: '5px' }}>
                    {renderInlineFormatting(formattedLine)}
                </div>
            );
        } else if (isBullet) {
            return (
                <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    paddingLeft: '8px'
                }}>
                    <span style={{
                        marginRight: '8px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: 'inherit'
                    }}>•</span>
                    <span style={{ lineHeight: '1.6' }}>{renderInlineFormatting(formattedLine)}</span>
                </div>
            );
        } else {
            return (
                <React.Fragment key={index}>
                    {renderInlineFormatting(formattedLine)}
                    {index < arr.length - 1 && <br />}
                </React.Fragment>
            );
        }
    });
};

// Typing effect component for bot messages
const TypingText = ({ text, speed = 5, onTypingComplete, onCharacterAdded }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef(null);

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
                // Trigger scroll on each character addition
                if (onCharacterAdded) {
                    onCharacterAdded();
                }
            }, speed);

            return () => clearTimeout(timeout);
        } else if (currentIndex === text.length && onTypingComplete) {
            onTypingComplete();
        }
    }, [currentIndex, text, speed, onTypingComplete, onCharacterAdded]);

    return (
        <span ref={containerRef}>
            {formatResponse(displayedText)}
        </span>
    );
};

const ChatWidget = () => {
    // Load messages from localStorage or use default
    const user_id = localStorage.getItem('id');
    const getInitialMessages = () => {
        const savedMessages = localStorage.getItem(`chat_messages_${user_id}`);
        if (savedMessages) {
            try {
                return JSON.parse(savedMessages);
            } catch (e) {
                return [
                    {
                        id: 1,
                        text: "Hi there! 👋 Let me know if you have any questions—I'm here to help!",
                        isBot: true,
                        timestamp: new Date(),
                    },
                ];
            }
        }
        return [
            {
                id: 1,
                text: "Hi there! 👋 Let me know if you have any questions—I'm here to help!",
                isBot: true,
                timestamp: new Date(),
            },
        ];
    };

    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState(getInitialMessages());
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showHelpText, setShowHelpText] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isHoveringIcon, setIsHoveringIcon] = useState(false);
    const [isTypingMessage, setIsTypingMessage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState('');
    const [isWsConnected, setIsWsConnected] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const chatRef = useRef(null);
    const ws = useRef(null);
    // Streaming state: id of the in-progress bot message, and its accumulated text
    const streamingMsgId = useRef(null);
    const streamingText = useRef('');

    const THEME_COLOR = "var(--bs-primary, #2196f3)";
    const THEME_GRADIENT = "var(--bs-primary, #2196f3)";
    const SUCCESS_COLOR = "#28c76f";
    const SHADOW_HEAVY = "0 8px 30px rgba(96, 156, 183, 0.2)";
    // Save messages to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(`chat_messages_${user_id}`, JSON.stringify(messages));
    }, [messages]);

    // Check if user is logged in and reset messages on logout
    useEffect(() => {
        const checkLoginStatus = () => {
            const isLoggedIn = localStorage.getItem('Login_Status');
            const loginToken = localStorage.getItem('login');
            const wasLoggedIn = isLoggedIn === 'Yes' || !!loginToken;

            if (wasLoggedIn) {
                setIsLoggedIn(true);
            } else {
                setIsLoggedIn(false);
                // Reset messages to initial state when logged out
                const initialMessages = [
                    {
                        id: 1,
                        text: "Hi there! 👋 Let me know if you have any questions — I'm here to help!",
                        isBot: true,
                        timestamp: new Date(),
                    },
                ];
                setMessages(initialMessages);
                // Clear messages from localStorage
                localStorage.removeItem(`chat_messages_${user_id}`);
                // Close chat widget if open
                setIsExpanded(false);
            }
        };

        // Check on mount
        checkLoginStatus();

        // Listen for storage changes (login/logout events)
        window.addEventListener('storage', checkLoginStatus);

        // Check periodically in case of same-tab changes
        const interval = setInterval(checkLoginStatus, 1000);

        return () => {
            window.removeEventListener('storage', checkLoginStatus);
            clearInterval(interval);
        };
    }, []);
    // WebSocket Connection Management
    useEffect(() => {
        if (!isLoggedIn) return;

        let wsInstance = null;

        const initWebSocket = async () => {
            let token = localStorage.getItem('helpchat_token');

            // If token is missing or invalid on refresh, fetch a fresh one
            if (!token || token === 'null' || token === 'undefined') {
                try {
                    const userId = localStorage.getItem('id');
                    const webservice = window.location.origin;
                    const authHost = BotServicePath;

                    const triggerResult = await fetch(`${authHost}/auth-health`, {
                        method: 'POST',
                        body: JSON.stringify({ user_Id: userId, source: webservice }),
                        headers: { 'Content-type': 'application/json; charset=UTF-8' }
                    });
                    const triggerResponse = await triggerResult.json();
                    if (triggerResponse && triggerResponse.access_token) {
                        token = triggerResponse.access_token;
                        localStorage.setItem('helpchat_token', token);
                    } else {
                        console.error('Could not fetch helpchat_token:', triggerResponse);
                        return; // No valid token — don't connect
                    }
                } catch (error) {
                    console.error('Error fetching helpchat_token on refresh:', error);
                    return;
                }
            }

            // Connect WebSocket dynamically based on chatservice URL
            const wsHost = BotWsPath;

            const wsUrl = `${wsHost}/ws/help-chat/${token}`;
            wsInstance = new WebSocket(wsUrl);
            ws.current = wsInstance;

            wsInstance.onopen = () => {
                console.log('Chat WebSocket connected');
                setIsWsConnected(true);
            };

            wsInstance.onmessage = (event) => {
                let data;
                try {
                    data = JSON.parse(event.data);
                    console.log('ws data', data)
                } catch (e) {
                    data = { type: 'response', response: event.data };
                    console.log('ws data catch', data)
                }

                // --- Streaming token ---
                if (data.type === 'stream' && data.token) {
                    const token = data.token;

                    if (!streamingMsgId.current) {
                        // Create a new bot message bubble for this stream
                        const newId = Date.now();
                        streamingMsgId.current = newId;
                        streamingText.current = token;
                        setIsLoading(false);
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: newId,
                                text: token,
                                isBot: true,
                                isStreaming: true,
                                timestamp: new Date(),
                            },
                        ]);
                    } else {
                        // Append token to existing streaming bubble
                        streamingText.current += token;
                        const currentId = streamingMsgId.current;
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === currentId
                                    ? { ...msg, text: streamingText.current }
                                    : msg
                            )
                        );
                    }
                    scrollToBottom(true);
                    return;
                }

                // --- Stream ended ---
                if (data.type === 'end') {
                    const finalText = data.response || streamingText.current;
                    const currentId = streamingMsgId.current;
                    if (currentId) {
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === currentId
                                    ? { ...msg, text: finalText || msg.text, isStreaming: false }
                                    : msg
                            )
                        );
                    }
                    streamingMsgId.current = null;
                    streamingText.current = '';
                    setIsLoading(false);
                    return;
                }

                // --- Non-streaming / fallback response ---
                const botResponseText = data.response || data.message || data.text;
                if (botResponseText) {
                    if (streamingMsgId.current) {
                        const currentId = streamingMsgId.current;
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === currentId
                                    ? { ...msg, text: botResponseText, isStreaming: false }
                                    : msg
                            )
                        );
                        streamingMsgId.current = null;
                        streamingText.current = '';
                    } else {
                        setMessages((prev) => {
                            const newBotMessage = {
                                id: Date.now(),
                                text: typeof botResponseText === 'string' ? botResponseText : JSON.stringify(botResponseText),
                                isBot: true,
                                timestamp: new Date(),
                            };
                            setIsTypingMessage(true);
                            setCurrentIndex(newBotMessage.id);
                            return [...prev, newBotMessage];
                        });
                    }
                    setIsLoading(false);
                }
            };

            wsInstance.onerror = (error) => {
                console.error('Chat WebSocket error:', error);
                setIsLoading(false);
            };

            wsInstance.onclose = () => {
                console.log('Chat WebSocket disconnected');
                setIsWsConnected(false);
            };
        };

        initWebSocket();

        return () => {
            if (wsInstance) {
                wsInstance.close();
            }
        };
    }, [isLoggedIn]);

    // Click outside to close chat
    useEffect(() => {
        if (!isExpanded) return;

        const handleClickOutside = (event) => {
            if (chatRef.current && !chatRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);

    const scrollToBottom = (smooth = true) => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: smooth ? "smooth" : "auto",
                block: "end"
            });
        }
    };

    // Smooth scroll during typing
    useEffect(() => {
        if (isTypingMessage) {

            scrollToBottom();
        }
    }, [isTypingMessage]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    // Scroll to bottom when chat is opened
    useEffect(() => {
        if (isExpanded) {
            // Use setTimeout to ensure DOM is rendered
            setTimeout(() => {
                scrollToBottom(false); // Instant scroll on open
            }, 0);
        }
    }, [isExpanded]);

    // Initialize menu tracking on component mount
    useEffect(() => {
        setupMenuTracking();
    }, []);

    // Show help text only when hovering over the icon and chat is not expanded
    useEffect(() => {
        if (!isExpanded && isHoveringIcon) {
            setShowHelpText(true);
        } else {
            setShowHelpText(false);
        }
    }, [isExpanded, isHoveringIcon]);

    // Hide help text when chat is expanded
    useEffect(() => {
        if (isExpanded) {
            setShowHelpText(false);
            setIsHoveringIcon(false);
        }
    }, [isExpanded]);

    const formatTime = (date) => {
        const d = new Date(date);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleScroll = () => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
        setShowScrollDown(!isAtBottom);
    };

    // Function to get current user ID
    const getCurrentUserId = () => {
        const storedUserId = localStorage.getItem('id');
        if (storedUserId) return storedUserId;
        return null;
    };

    // Function to get current page/menu from menu buttons
    const getCurrentPage = () => {
        // Try to get from sidebar navigation first
        const activeNavItem = document.querySelector('.navigation-main .nav-item.active .menu-link, .navigation-main .active');
        if (activeNavItem) {
            const menuText = activeNavItem.textContent?.trim();
            if (menuText) return menuText;
        }

        // Check for active sidebar group
        const activeSidebarGroup = document.querySelector('.navigation-main .sidebar-group-active');
        if (activeSidebarGroup) {
            const groupText = activeSidebarGroup.querySelector('span')?.textContent?.trim();
            if (groupText) return groupText;
        }

        // Check localStorage for selected menu
        const selectedMenu = localStorage.getItem('selected_menu');
        if (selectedMenu) return selectedMenu;

        // Fallback to URL path
        const currentPath = window.location.pathname;
        const pathSegments = currentPath.split('/').filter(segment => segment);
        if (pathSegments.length > 0) {
            return pathSegments[pathSegments.length - 1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }

        return 'Dashboard';
    };

    // Function to set up menu button tracking
    const setupMenuTracking = () => {
        const menuSelectors = [
            '.menu-button',
            '.nav-button',
            '.menu-item',
            '.nav-item',
            '.menu',
            'button[data-menu]',
            'button[data-page]',
            'a[data-menu]',
            'a[data-page]'
        ];

        menuSelectors.forEach(selector => {
            document.addEventListener('click', (e) => {
                const clickedElement = e.target.closest(selector);
                if (clickedElement) {
                    const menuName = clickedElement.textContent.trim() ||
                        clickedElement.getAttribute('data-menu') ||
                        clickedElement.getAttribute('data-page') ||
                        clickedElement.getAttribute('title') ||
                        clickedElement.id;

                    if (menuName) {
                        localStorage.setItem('selected_menu', menuName);
                        window.currentPage = menuName;

                        window.dispatchEvent(new CustomEvent('menuChanged', {
                            detail: { menu: menuName }
                        }));
                    }
                }
            });
        });
    };
    // console.log(localStorage);

    const getUserPermissions = async () => {

        try {
            // Fetch permissions from localStorage (saved by LayoutRoutes)
            const storedPermissions = localStorage.getItem("Permission");
            const userPermissions = storedPermissions ? JSON.parse(storedPermissions) : [];

            // Dentagenix already stores the full list of allowed menus and actions in localStorage
            const structuredPermissions = userPermissions.map(menu => {
                const actionStatus = {
                    add: 'No',
                    edit: 'No',
                    delete: 'No',
                    view: 'No',
                    download: 'No',
                    upload: 'No',
                    restart: 'No'
                };

                if (Array.isArray(menu.Menu_Action)) {
                    menu.Menu_Action.forEach(action => {
                        actionStatus[action] = 'Yes';
                    });
                }

                return {
                    menuName: menu.Menu_Label,
                    menuAction: actionStatus
                };
            });

            return structuredPermissions;
        } catch (e) {
            console.error('Error fetching/processing permissions:', e);
            return [];
        }
    };

    const handleSendMessage = async () => {
        // Block if bot is still replying
        if (isLoading || streamingMsgId.current) return;

        const permission = await getUserPermissions()
        console.log("-------------------------", permission);

        if (inputValue.trim()) {
            const newMessage = {
                id: Date.now(),
                text: inputValue,
                isBot: false,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, newMessage]);
            setInputValue("");
            setIsLoading(true);

            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                const token = localStorage.getItem('helpchat_token');
                ws.current.send(JSON.stringify({
                    "token": token,
                    "user_id": getCurrentUserId(),
                    "question": inputValue,
                    "screen_name": getCurrentPage(),
                    "permissions": permission
                }));
            } else {
                const loginStatus = localStorage.getItem('Login_Status');
                if (loginStatus === 'Yes') {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: Date.now() + 1,
                            text: "Sorry, the chat is currently disconnected. Please try again later.",
                            isBot: true,
                            timestamp: new Date(),
                        },
                    ]);
                }
                setIsLoading(false);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !isLoading && !streamingMsgId.current) handleSendMessage();
    };

    // True while waiting for any bot response
    const isBotReplying = isLoading || !!streamingMsgId.current;

    const toggleWidget = () => setIsExpanded(!isExpanded);

    // Don't render chat widget if user is not logged in
    if (!isLoggedIn) {
        return null;
    }

    return (
        <div
            style={{
                position: "fixed",
                bottom: "8px",
                right: "20px",
                zIndex: 1000,
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            }}
        >
            {!isExpanded && (
                <div
                    onClick={toggleWidget}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#001b48",
                        color: "white",
                        padding: "7px 16px",
                        borderRadius: "20px",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(0, 27, 72, 0.35)",
                        transition: "all 0.2s ease-in-out",
                        border: "1px solid rgba(255, 255, 255, 0.15)"
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.04)";
                        e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 27, 72, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 27, 72, 0.35)";
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", letterSpacing: "0.2px" }}>
                        Help Chat
                    </span>
                    <span style={{
                        width: "9px",
                        height: "9px",
                        backgroundColor: "#00d254",
                        borderRadius: "50%",
                        boxShadow: "0 0 6px rgba(0, 210, 84, 0.8)",
                        display: "inline-block",
                        marginLeft: "2px"
                    }} />
                </div>
            )}
            <style>{`
            @keyframes slideInBounce {
              0% {
                opacity: 0;
                transform: translateY(-20px) scale(0.3);
              }
              50% {
                opacity: 0.8;
                transform: translateY(-5px) scale(1.05);
              }
              70% {
                opacity: 1;
                transform: translateY(2px) scale(0.95);
              }
              100% {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            
            @keyframes pulseGlow {
              0%, 100% {
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                transform: scale(1);
              }
              25% {
                box-shadow: 0 8px 30px rgba(221, 16, 16, 0.4);
                transform: scale(1.02);
              }
              50% {
                box-shadow: 0 12px 40px rgba(221, 16, 16, 0.6);
                transform: scale(1.05);
              }
              75% {
                box-shadow: 0 8px 30px rgba(221, 16, 16, 0.4);
                transform: scale(1.02);
              }
            }
            
            @keyframes chatWindowSlideUp {
              0% {
                opacity: 0;
                transform: translateY(30px) scale(0.95);
              }
              100% {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.7;
                transform: scale(1.2);
              }
            }
            
            @keyframes bubbleSlideInBounce {
              0% {
                opacity: 0;
                transform: translateY(-20px) translateX(0) scale(0.8) rotate(0deg);
              }
              20% {
                opacity: 0.3;
                transform: translateY(-35px) translateX(-8px) scale(0.6) rotate(-8deg);
              }
              50% {
                opacity: 0.8;
                transform: translateY(-5px) translateX(3px) scale(1.15) rotate(3deg);
              }
              70% {
                opacity: 0.95;
                transform: translateY(3px) translateX(-1px) scale(0.95) rotate(-1deg);
              }
              90% {
                opacity: 1;
                transform: translateY(-1px) translateX(0) scale(1.02) rotate(0.5deg);
              }
              100% {
                opacity: 1;
                transform: translateY(0) translateX(0) scale(1) rotate(0deg);
              }
            }

            @keyframes bubbleSlideUpFromIcon {
              0% {
                opacity: 0;
                transform: translateY(50px) scale(0.3);
              }
              50% {
                opacity: 0.8;
                transform: translateY(-5px) scale(1.05);
              }
              70% {
                opacity: 1;
                transform: translateY(2px) scale(0.98);
              }
              100% {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }

            @keyframes typingText {
              0% {
                width: 0;
              }
              100% {
                width: 100%;
              }
            }

            @keyframes blinkCursor {
              0%, 100% {
                border-right-color: transparent;
              }
              50% {
                border-right-color: ${THEME_COLOR};
              }
            }
            
            @keyframes bubbleBreathing {
              0%, 100% {
                transform: translateY(0) scale(1) rotate(0deg);
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9);
              }
              50% {
                transform: translateY(-3px) scale(1.03) rotate(0.5deg);
                box-shadow: 0 15px 40px rgba(96, 156, 183, 0.2), 0 8px 20px rgba(96, 156, 183, 0.15), inset 0 1px 0 rgba(255, 255, 255, 1);
              }
            }
            
            @keyframes iconGentlePulse {
              0%, 100% {
                transform: scale(1) translateY(0) rotate(0deg);
                box-shadow: 0 8px 30px rgba(96, 156, 183, 0.15);
                border-color: rgba(255, 255, 255, 0.3);
              }
              50% {
                transform: scale(1.02) translateY(-0.5px) rotate(0deg);
                box-shadow: 0 10px 32px rgba(96, 156, 183, 0.2);
                border-color: rgba(255, 255, 255, 0.35);
              }
            }
            
            @keyframes iconPulseWithBubble {
              0%, 100% {
                transform: scale(1) translateY(0) rotate(0deg);
                box-shadow: 0 8px 30px rgba(96, 156, 183, 0.3);
                border-color: rgba(255, 255, 255, 0.4);
              }
              25% {
                transform: scale(1.05) translateY(-2px) rotate(1deg);
                box-shadow: 0 12px 40px rgba(96, 156, 183, 0.4);
                border-color: rgba(255, 255, 255, 0.5);
              }
              50% {
                transform: scale(1.1) translateY(-4px) rotate(0deg);
                box-shadow: 0 18px 50px rgba(96, 156, 183, 0.5);
                border-color: rgba(255, 255, 255, 0.6);
              }
              75% {
                transform: scale(1.05) translateY(-2px) rotate(-1deg);
                box-shadow: 0 12px 40px rgba(96, 156, 183, 0.4);
                border-color: rgba(255, 255, 255, 0.5);
              }
            }
            
            @keyframes typingDots {
              0%, 60%, 100% {
                opacity: 0.4;
                transform: scale(1);
              }
              30% {
                opacity: 1;
                transform: scale(1.4);
              }
            }
          `}</style>
            {/* Chat Window */}
            {isExpanded && (
                <div
                    ref={chatRef}
                    style={{
                        width: "580px",
                        height: "650px",
                        background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                        borderRadius: "24px",
                        boxShadow: "0 25px 80px rgba(0, 0, 0, 0.15), 0 10px 30px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        border: "2px solid rgba(255, 255, 255, 0.3)",
                        position: "fixed",
                        bottom: "50px",
                        right: "20px",
                        transform: "none",
                        backdropFilter: "blur(25px)",
                        animation: "chatWindowSlideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards"
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #149ddd 50%, ${THEME_COLOR} 100%)`,
                            color: "white",
                            padding: "18px 20px", // Reduced from 24px
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: "22px 22px 0 0",
                            boxShadow: "0 4px 20px rgba(96, 156, 183, 0.3)"
                        }}
                    >
                        <>
                            <div style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                            }} />
                            <div style={{ display: "flex", alignItems: "center", gap: "16px", zIndex: 1 }}>
                                <div style={{
                                    width: "44px",
                                    height: "44px",
                                    background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)",
                                    borderRadius: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px solid rgba(255,255,255,0.3)",
                                    backdropFilter: "blur(10px)"
                                }}>
                                    <span style={{ fontSize: "20px" }}>🤖</span>
                                </div>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <span style={{
                                            margin: 0,
                                            fontSize: "18px",
                                            fontWeight: "700",
                                            color: 'white',
                                            letterSpacing: "0.3px"
                                        }}>
                                            AI Assistant
                                        </span>
                                        <div style={{
                                            width: "6px",
                                            height: "6px",
                                            backgroundColor: isWsConnected ? SUCCESS_COLOR : "#ea5455", // Red for offline
                                            borderRadius: "50%",
                                            animation: isWsConnected ? "pulse 2s infinite" : "none",
                                            boxShadow: isWsConnected ? "0 0 8px rgba(40, 167, 69, 0.6)" : "0 0 8px rgba(234, 84, 85, 0.6)"
                                        }} />
                                    </div>
                                    <span style={{
                                        fontSize: "12px",
                                        color: isWsConnected ? "rgba(255,255,255,0.8)" : "#ea5455",
                                        fontWeight: isWsConnected ? "400" : "600"
                                    }}>
                                        {isWsConnected ? "Online now" : "Offline"}
                                    </span>
                                </div>
                            </div>
                        </>
                        <button
                            onClick={toggleWidget}
                            style={{
                                background: "rgba(255, 255, 255, 0.15)",
                                border: "1px solid rgba(255, 255, 255, 0.3)",
                                color: "white",
                                fontSize: "14px",
                                cursor: "pointer",
                                padding: "0",
                                borderRadius: "10px",
                                transition: "all 0.3s ease",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1,
                                backdropFilter: "blur(10px)"
                            }}
                        >
                            ✖
                        </button>
                    </div>

                    {/* Messages Container */}
                    <div
                        ref={messagesContainerRef}
                        onScroll={handleScroll}
                        style={{
                            flex: 1,
                            padding: "15px", // Reduced from 20px
                            overflowY: "auto",
                            background: "linear-gradient(to bottom, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            gap: "8px", // Reduced from 12px
                            scrollbarWidth: "thin",
                            scrollbarColor: `${THEME_COLOR}20 transparent`
                        }}
                    >
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    marginBottom: "10px", // Reduced from 15px
                                    flexDirection: message.isBot ? "row" : "row-reverse",
                                }}
                            >
                                {/* Avatar */}
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: message.isBot ? THEME_COLOR : "#00c2ff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: message.isBot ? "0 10px 0 0" : "0 0 0 10px",
                                        flexShrink: 0,
                                    }}
                                >
                                    <span style={{ color: "white", fontSize: "14px" }}>
                                        {message.isBot ? "🤖" : "👤"}
                                    </span>
                                </div>

                                {/* Message Bubble */}
                                <div
                                    style={{
                                        background: message.isBot
                                            ? "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)"
                                            : `linear-gradient(135deg, ${THEME_COLOR} 0%, #149ddd 100%)`,
                                        color: message.isBot ? "#22292f" : "white",
                                        padding: "14px 18px",
                                        borderRadius: message.isBot ? "18px 18px 18px 4px" : "18px 18px 4px 18px",
                                        maxWidth: "80%",
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        boxShadow: message.isBot
                                            ? "0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)"
                                            : "0 4px 16px rgba(96, 156, 183, 0.3)",
                                        border: message.isBot ? "1px solid rgba(0,0,0,0.05)" : "none",
                                        fontWeight: "400",
                                        position: "relative"
                                    }}
                                >
                                    {message.isBot && message.isStreaming ? (
                                        // Live streaming: show text as it arrives with a blinking cursor
                                        <span>
                                            {formatResponse(message.text)}
                                            <span style={{
                                                display: 'inline-block',
                                                width: '2px',
                                                height: '1em',
                                                backgroundColor: 'currentColor',
                                                marginLeft: '2px',
                                                verticalAlign: 'text-bottom',
                                                animation: 'blinkCursor 0.8s step-end infinite'
                                            }} />
                                        </span>
                                    ) : message.isBot && isTypingMessage && currentIndex === message.id ? (
                                        <TypingText
                                            text={message.text}
                                            speed={5}
                                            onTypingComplete={() => setIsTypingMessage(false)}
                                            onCharacterAdded={() => scrollToBottom(true)}
                                        />
                                    ) : (
                                        formatResponse(message.text)
                                    )}
                                    <div
                                        style={{
                                            fontSize: "11px",
                                            color: message.isBot ? "#888" : "#000",
                                            marginTop: "3px",
                                            textAlign: "right"
                                        }}
                                    >
                                        {formatTime(message.timestamp)}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Loading Indicator */}
                        {isLoading && (
                            <div
                                style={{
                                    display: "flex",
                                    marginBottom: "15px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: THEME_COLOR,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "10px",
                                    }}
                                >
                                    <span style={{ color: "white", fontSize: "14px" }}>🤖</span>
                                </div>

                                <div
                                    style={{
                                        backgroundColor: "#f3f3f3",
                                        color: "#000",
                                        padding: "10px 15px",
                                        borderRadius: "18px",
                                        maxWidth: "70%",
                                        fontSize: "14px",
                                        lineHeight: "1.4",
                                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                                    }}
                                >
                                    Analyzing...
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div
                        style={{
                            padding: "15px", // Reduced from 20px
                            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                            background: "linear-gradient(to top, #ffffff 0%, #f8f9fa 100%)",
                            backdropFilter: "blur(15px)",
                            borderRadius: "0 0 22px 22px"
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                borderRadius: "20px", // Reduced from 25px
                                padding: "10px 16px", // Reduced from 14px 20px
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
                                border: "2px solid rgba(96, 156, 183, 0.1)",
                                transition: "all 0.3s ease"
                            }}
                        >
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => !isBotReplying && setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={isBotReplying ? "Waiting for response..." : "Type your message…"}
                                disabled={isBotReplying}
                                style={{
                                    flex: 1,
                                    border: "none",
                                    backgroundColor: "transparent",
                                    outline: "none",
                                    fontSize: "14px",
                                    padding: "6px 0",
                                    color: isBotReplying ? "#aaa" : "#22292f",
                                    fontWeight: "400",
                                    cursor: isBotReplying ? "not-allowed" : "text"
                                }}
                            />

                            <button
                                onClick={handleSendMessage}
                                disabled={isBotReplying}
                                style={{
                                    background: isBotReplying
                                        ? "linear-gradient(135deg, #ccc 0%, #bbb 100%)"
                                        : `linear-gradient(135deg, ${THEME_COLOR} 0%, #149ddd 100%)`,
                                    border: "none",
                                    color: "white",
                                    fontSize: "18px",
                                    cursor: isBotReplying ? "not-allowed" : "pointer",
                                    padding: "0",
                                    marginLeft: "12px",
                                    borderRadius: "50%",
                                    boxShadow: isBotReplying ? "none" : "0 4px 16px rgba(96, 156, 183, 0.3)",
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    width: "44px",
                                    height: "44px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: isBotReplying ? 0.5 : 1
                                }}
                            >
                                ➤
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatWidget;
